// Energy-value PROVENANCE — the calorie-specific layer over the metric-agnostic
// source registry (`metricSources.ts`, measurement-contract §2). A workout's
// calories can come from more than one source — JL's own estimate, a value
// measured through Health Connect (produced by Zepp / Google Fit / …), and, in
// future, other direct 3rd-party integrations.
//
// The generic "which source produced a measured value" lookup lives in
// `metricSources.ts` (a device origin names its producer regardless of metric).
// This module owns ONLY what is genuinely calorie-shaped: JL's own calorie
// estimate as a source, and the provenance ROW whose `kcal` field couples it to
// `ResolvedWorkoutCalories`. A second metric (HR, steps) would add its own thin
// layer here — it does NOT reshape the generic registry.

import {
  metricSourceForOrigin,
  type MetricSourceKind,
  type MetricSourceRef,
} from "./metricSources.js";
import type { ResolvedWorkoutCalories } from "./resolveWorkoutCalories.js";

// Back-compat aliases: the calorie layer's original public type names, now thin
// aliases of the generic ones so existing consumers keep their imports unchanged.
export type EnergySourceKind = MetricSourceKind;
export type EnergySourceRef = MetricSourceRef;

/** Resolve the source of a MEASURED calorie value from its HC dataOrigin. Kept as
 *  the calorie layer's original public name — a genuine re-export of the generic
 *  lookup, not a second wrapper (one source of truth). */
export { metricSourceForOrigin as measuredEnergySource } from "./metricSources.js";

/** JL's own computed estimate. The Keytel/MET `method` is internal provenance,
 *  not surfaced as a word (no "estimated" commentary) — the source is simply JL.
 *  Calorie-specific: no metric-agnostic equivalent is defined yet, so it stays in
 *  this layer rather than being generalised for a caller that doesn't exist. */
export const JL_ESTIMATE_SOURCE: MetricSourceRef = {
  id: "jl:estimate",
  labelKey: "mobile.energySource.jlEstimate",
  kind: "estimate",
};

export interface EnergyProvenanceRow {
  source: MetricSourceRef;
  kcal: number;
  /** True for the figure the read-time resolver picked as canonical. */
  active: boolean;
}

/**
 * The rows an (i) provenance pill renders for a resolved calorie value: one per
 * figure the session actually carries (measured and/or estimate), each with its
 * source ref, its kcal, and whether it's the active (canonical) one. Pure — the
 * component just maps `t(row.source.labelKey)`; no source logic in the UI.
 */
export const energyProvenanceRows = (
  resolved: ResolvedWorkoutCalories,
  externalOrigin?: string,
): EnergyProvenanceRow[] => {
  const rows: EnergyProvenanceRow[] = [];
  if (resolved.measured !== undefined) {
    rows.push({
      source: metricSourceForOrigin(externalOrigin),
      kcal: resolved.measured,
      active: resolved.source === "hc",
    });
  }
  if (resolved.estimate !== undefined) {
    rows.push({
      source: JL_ESTIMATE_SOURCE,
      kcal: resolved.estimate.kcal,
      active: resolved.source === "jl:estimate",
    });
  }
  return rows;
};
